import type { ConnectorBrandIconName } from '../components/ui/brand/ConnectorBrandIcon';

/**
 * Provider-level connector presentation used wherever users choose or inspect
 * a unified Workspace connection. OAuth and tool allowlists stay in shared /
 * backend code; this file only keeps the human-facing contract consistent.
 */

export interface WorkspaceConnectorCapability {
  readonly label: string;
  readonly productName: string;
  readonly icon: ConnectorBrandIconName;
  readonly action: string;
}

export interface WorkspaceConnectorPresentation {
  readonly name: string;
  readonly icon: ConnectorBrandIconName;
  readonly summary: string;
  readonly chatSummary: string;
  readonly capabilities: readonly WorkspaceConnectorCapability[];
}

export const WORKSPACE_CONNECTOR_PRESENTATIONS: Readonly<
  Record<'googlesuper' | 'microsoft_workspace', WorkspaceConnectorPresentation>
> = {
  googlesuper: {
    name: 'Google Workspace',
    icon: 'google',
    summary: 'Read-only search across the work you already keep in Google Workspace.',
    chatSummary: 'Drive · Docs · Sheets · Slides',
    capabilities: [
      {
        label: 'Drive',
        productName: 'Google Drive',
        icon: 'google-drive',
        action: 'Included',
      },
      {
        label: 'Docs',
        productName: 'Google Docs',
        icon: 'google-docs',
        action: 'Included',
      },
      {
        label: 'Sheets',
        productName: 'Google Sheets',
        icon: 'google-sheets',
        action: 'Included',
      },
      {
        label: 'Slides',
        productName: 'Google Slides',
        icon: 'google-slides',
        action: 'Included',
      },
    ],
  },
  microsoft_workspace: {
    name: 'Microsoft Workspace',
    icon: 'microsoft',
    summary: 'Search and read files across OneDrive and SharePoint. Import OneNote on demand.',
    chatSummary: 'OneDrive · SharePoint · Word and Excel files · OneNote import',
    capabilities: [
      {
        label: 'OneDrive',
        productName: 'Microsoft OneDrive',
        icon: 'microsoft-onedrive',
        action: 'Search',
      },
      {
        label: 'SharePoint',
        productName: 'Microsoft SharePoint',
        icon: 'microsoft-sharepoint',
        action: 'Search',
      },
      {
        label: 'Word',
        productName: 'Microsoft Word',
        icon: 'microsoft-word',
        action: 'Read documents',
      },
      {
        label: 'Excel',
        productName: 'Microsoft Excel',
        icon: 'microsoft-excel',
        action: 'Read sheets',
      },
      {
        label: 'OneNote',
        productName: 'Microsoft OneNote',
        icon: 'microsoft-onenote',
        action: 'Import on demand',
      },
    ],
  },
};

export function workspaceConnectorPresentationFor(
  toolkitKey: string,
): WorkspaceConnectorPresentation | undefined {
  return WORKSPACE_CONNECTOR_PRESENTATIONS[
    toolkitKey as keyof typeof WORKSPACE_CONNECTOR_PRESENTATIONS
  ];
}
